import { createBrowserHistory } from 'history';
import { applyMiddleware, compose, createStore } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension';
import { createReduxHistoryContext } from 'redux-first-history';
import createRootReducer from './root.reducers';
import logger from 'redux-logger';
import createSagaMiddleware from 'redux-saga';
import AsyncStorage from '@react-native-community/async-storage';
import { persistStore, persistReducer } from 'redux-persist';

const persistConfig = {
    key: 'envago:ksa:pro',
    storage: AsyncStorage,
    whitelist: ['login', 'queue', 'jobs', 'help'],
};

// Token links are sometimes opened with a double slash (https://host//m/<token>); React Router
// no longer matches '//m/:token', so normalize the path before the history is created.
if (window.location.pathname.startsWith('//')) {
    window.history.replaceState(
        window.history.state,
        '',
        window.location.pathname.replace(/^\/{2,}/, '/') + window.location.search + window.location.hash,
    );
}

// Router state in redux + push()/replace()/goBack() actions (e.g. from sagas)
const { createReduxHistory, routerMiddleware, routerReducer } = createReduxHistoryContext({ history: createBrowserHistory() });

function configureStore(preloadedState) {
    const sagaMiddleware = createSagaMiddleware();

    // Tracks the last dispatched action across ALL dispatch paths, including saga yield put.
    // The store.dispatch patch only intercepts direct calls; yield put bypasses it by going through
    // the internal applyMiddleware dispatch closure. Placing tracking inside applyMiddleware ensures
    // every action — regardless of origin — is captured before subscribers fire.
    let lastAction = null;
    const trackLastAction = () => next => action => {
        lastAction = action;
        return next(action);
    };

    const persistedReducer = persistReducer(persistConfig, createRootReducer(routerReducer));

    let store;
    if (process.env.NODE_ENV === 'production') {
        store = createStore(
            persistedReducer, // root reducer with router state
            preloadedState,
            compose(
                applyMiddleware(
                    trackLastAction,
                    routerMiddleware, // for dispatching history actions
                    // ... other middlewares ...
                    sagaMiddleware,
                ),
            ),
        );
    } else {
        store = createStore(
            persistedReducer, // root reducer with router state
            preloadedState,
            composeWithDevTools(
                applyMiddleware(
                    trackLastAction,
                    logger,
                    routerMiddleware, // for dispatching history actions
                    // ... other middlewares ...
                    sagaMiddleware,
                ),
            ),
        );
    }

    Object.defineProperty(store, 'lastAction', {
        get: () => lastAction,
        set: v => { lastAction = v; },
        configurable: true,
    });

    const persistor = persistStore(store);

    return {
        store,
        persistor,
        runSaga: sagaMiddleware.run,
    };
}

export const { store, runSaga, persistor } = configureStore();
export const history = createReduxHistory(store);
